import React, { useEffect } from "react";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, ShieldCheck, Star, Users, Quote,
  MapPin, Phone, Mail, Sparkles, ClipboardCheck, Stethoscope,
} from "lucide-react";
import { SERVICE_BY_SLUG } from "../data/services";
import { LANDING_CONTENT, LANDING_CITIES, LANDING_SLUGS } from "../data/landingContent";
import { COMMON_FAQS } from "../data/services";
import { VIDEOS } from "../data/videos";
import { useSEO, serviceSchema, breadcrumbSchema, SITE_URL } from "../lib/seo";
import { trackPhoneClick } from "../lib/tracking";
import VideoSection from "../components/VideoSection";

function setMeta({ title, description, keywords }) {
  if (typeof document === "undefined") return;
  if (title) document.title = title;
  const ensure = (name, attr = "name") => {
    let m = document.querySelector(`meta[${attr}="${name}"]`);
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute(attr, name);
      document.head.appendChild(m);
    }
    return m;
  };
  if (description) ensure("description").setAttribute("content", description);
  if (keywords) ensure("keywords").setAttribute("content", keywords.join(", "));
}

function parseFromPath(pathname) {
  // /slug or /slug-city — sorted to match longest slug first
  const path = pathname.replace(/^\//, "").replace(/\/$/, "");
  const sorted = [...LANDING_SLUGS].sort((a, b) => b.length - a.length);
  for (const slug of sorted) {
    if (path === slug) return { slug, city: null };
    for (const c of Object.values(LANDING_CITIES)) {
      if (path === `${slug}-${c.slug}`) return { slug, city: c.slug };
    }
  }
  return { slug: null, city: null };
}

export default function LandingPage() {
  const { pathname } = useLocation();
  const params = useParams();
  const parsed = params.slug ? { slug: params.slug, city: params.city || null } : parseFromPath(pathname);
  const { slug, city: citySlug } = parsed;

  const base = SERVICE_BY_SLUG[slug];
  const extra = LANDING_CONTENT[slug];
  const city = citySlug ? LANDING_CITIES[citySlug] : null;
  const Icon = base?.Icon;

  const locationLabel = city ? city.name : "Melbourne & Perth";
  const metaTitle = base && extra ? (city
    ? `${base.short} in ${city.name} | Alpha Community Care NDIS`
    : extra.metaTitle) : "";
  const metaDescription = base && extra ? (city
    ? `${extra.heroBlurb} Available in ${city.name} and across ${city.state}. Call ${city.phone}.`
    : extra.metaDescription) : "";

  useEffect(() => {
    if (base && extra) setMeta({ title: metaTitle, description: metaDescription, keywords: extra.keywords });
  }, [metaTitle, metaDescription, base, extra]);

  const canonical = base && extra
    ? `${SITE_URL}${city ? `/${slug}-${city.slug}` : `/${slug}`}`
    : null;
  useSEO({
    title: metaTitle,
    description: metaDescription,
    keywords: extra?.keywords,
    canonical,
    jsonLd: base && extra ? [
      serviceSchema({
        name: base.title,
        description: extra.heroBlurb,
        slug,
        city,
      }),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Services", path: "/services" },
        { name: base.short, path: `/${slug}` },
        ...(city ? [{ name: city.name, path: `/${slug}-${city.slug}` }] : []),
      ]),
    ] : [],
  });

  if (!slug || !LANDING_SLUGS.includes(slug) || !base || !extra) {
    return <Navigate to={slug ? `/services/${slug}` : "/services"} replace />;
  }

  return (
    <div data-testid={`landing-${slug}${city ? "-" + city.slug : ""}`}>
      {/* Hero */}
      <section className="relative overflow-hidden alpha-gradient-hero alpha-noise border-b border-[color:var(--alpha-line)]">
        <div className="alpha-orb alpha-orb-purple w-[300px] h-[300px] -left-20 top-10 opacity-70 hidden md:block" aria-hidden />
        <div className="alpha-orb alpha-orb-green w-[280px] h-[280px] right-[-30px] top-24 opacity-60 hidden md:block" aria-hidden />
        <div className="alpha-container pt-12 pb-16 lg:pt-20 lg:pb-24 relative">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--alpha-line-strong)] bg-white text-xs font-semibold text-[color:var(--alpha-purple-700)]">
                <ShieldCheck className="w-3.5 h-3.5" /> Registered NDIS Provider · <span>{locationLabel}</span>
              </div>
              <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[60px] font-display font-medium tracking-tight leading-[1.05]">
                {city ? `${base.short}` : extra.tagline} <span className="alpha-serif-italic alpha-gradient-text">{city ? `in ${city.name}` : ""}</span>
              </h1>
              <p className="mt-6 text-lg leading-relaxed text-[color:var(--alpha-mute)] max-w-xl">{extra.heroBlurb}</p>
              {city && <p className="mt-3 text-[color:var(--alpha-mute)] max-w-xl">{city.blurb}</p>}

              <div className="mt-7 flex flex-wrap gap-3">
                <Link to="/referrals" className="alpha-btn-primary" data-testid="landing-cta-refer">
                  Make a Referral <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${(city?.phone || "1300 380 692").replace(/\s/g, "")}`} onClick={() => trackPhoneClick(city?.phone || "1300 380 692", `landing-${slug}${city ? "-" + city.slug : ""}`)} className="alpha-btn-green" data-testid="landing-cta-call">
                  <Phone className="w-4 h-4" /> {city?.phone || "1300 380 692"}
                </a>
                <Link to="/contact" className="alpha-btn-ghost">Talk to intake</Link>
              </div>

              <div className="mt-8 flex flex-wrap gap-2">
                {(city?.nearby || ["Melbourne", "Docklands", "Perth", "West Perth"]).slice(0, 6).map((s) => (
                  <span key={s} className="alpha-chip-neutral alpha-chip"><MapPin className="w-3 h-3" /> {s}</span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="relative rounded-[28px] overflow-hidden aspect-[4/5] shadow-xl">
                <img src={extra.heroImage} alt={`${base.title} support`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E1832]/55 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 alpha-glass rounded-2xl p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full alpha-gradient text-white flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--alpha-mute)]">Service</div>
                    <div className="font-display font-semibold text-sm text-[color:var(--alpha-ink)]">{base.short}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="alpha-section">
        <div className="alpha-container">
          <div className="max-w-2xl">
            <div className="alpha-overline">Why Alpha {city ? `in ${city.name}` : ""}</div>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium tracking-tight">Everything you'd expect — and the things you didn't.</h2>
          </div>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {extra.benefits.map(([title, text], i) => (
              <div key={title} className={`alpha-card p-6 alpha-fade-up alpha-delay-${(i % 4) + 1}`}>
                <div className="w-10 h-10 rounded-xl bg-[color:var(--alpha-purple-100)] text-[color:var(--alpha-purple-700)] flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{title}</h3>
                <p className="text-sm text-[color:var(--alpha-mute)] mt-1 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for + How delivered (from services.js) */}
      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container grid lg:grid-cols-2 gap-10">
          <div>
            <div className="alpha-overline">Who it's for</div>
            <h2 className="mt-3 font-display text-3xl font-medium">Designed around real people, real needs.</h2>
            <ul className="mt-5 space-y-3">
              {base.forWho.map((w) => (
                <li key={w} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 mt-0.5 text-[color:var(--alpha-green-600)] shrink-0" /> <span>{w}</span></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="alpha-overline">How support is delivered</div>
            <h2 className="mt-3 font-display text-3xl font-medium">Considered, consistent, collaborative.</h2>
            <ul className="mt-5 space-y-3">
              {base.delivery.map((w) => (
                <li key={w} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 mt-0.5 text-[color:var(--alpha-green-600)] shrink-0" /> <span>{w}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Inclusions + Outcomes */}
      <section className="alpha-section">
        <div className="alpha-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 alpha-card p-8 bg-[color:var(--alpha-purple-50)] border-[color:var(--alpha-line-strong)]">
            <div className="alpha-overline">Key inclusions</div>
            <div className="mt-5 grid sm:grid-cols-2 gap-3">
              {base.inclusions.map((w) => (
                <div key={w} className="flex items-start gap-2 text-sm"><CheckCircle2 className="w-4 h-4 mt-0.5 text-[color:var(--alpha-purple-600)] shrink-0" /> {w}</div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="alpha-overline alpha-overline-green">Outcomes we aim for</div>
            <h3 className="mt-3 font-display text-2xl font-medium">What good support looks like.</h3>
            <ul className="mt-5 space-y-3">
              {extra.outcomes.map((o) => (
                <li key={o} className="flex items-start gap-3 text-sm"><Sparkles className="w-4 h-4 mt-0.5 text-[color:var(--alpha-green-600)] shrink-0" /> {o}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container">
          <div className="alpha-overline">How it works</div>
          <h2 className="mt-3 font-display text-3xl sm:text-[40px] font-medium">From first call to ongoing support.</h2>
          <ol className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              ["01", "Contact", "Call, email or submit a referral."],
              ["02", "Discovery", "We understand goals, clinical needs and preferences."],
              ["03", "Match", "We propose team, plan and (if needed) a home."],
              ["04", "Deliver", "Service starts. Regular reviews keep it on track."],
            ].map(([n, t, d]) => (
              <li key={n} className="alpha-card p-6">
                <div className="font-display text-3xl font-semibold alpha-gradient-text">{n}</div>
                <div className="mt-2 font-display text-lg font-semibold">{t}</div>
                <p className="text-sm text-[color:var(--alpha-mute)] mt-1">{d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Per-service explainer video — only renders if a matching video exists */}
      {VIDEOS[slug] && (
        <VideoSection
          src={VIDEOS[slug].url}
          eyebrow={`Watch · ${base.short.replace(/\s*\(.*?\)\s*/g, "")}`}
          title={`See ${base.short.replace(/\s*\(.*?\)\s*/g, "")} in action.`}
          subtitle={VIDEOS[slug].blurb}
          testid={`landing-video-${slug}`}
        />
      )}

      {/* Testimonial */}
      <section className="alpha-section">
        <div className="alpha-container max-w-4xl text-center">
          <Quote className="w-10 h-10 text-[color:var(--alpha-green-600)] mx-auto" />
          <blockquote className="mt-5 font-display text-2xl sm:text-3xl leading-tight italic text-[color:var(--alpha-ink)]">
            "Alpha took the time to actually understand what my brother needs. The team turn up, they communicate, they care. You can tell the difference."
          </blockquote>
          <div className="mt-5 text-sm text-[color:var(--alpha-mute)]">— Family member, {city?.name || "Melbourne"}</div>
          <div className="mt-3 flex justify-center gap-1 text-[color:var(--alpha-green-600)]">
            {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-current" />)}
          </div>
        </div>
      </section>

      {/* Trust row */}
      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            [ShieldCheck, "NDIS Registered", "Practice-Standard aligned"],
            [Users, "Qualified workforce", "Screened, trained, supervised"],
            [Stethoscope, "Clinical oversight", "Nursing & allied health"],
            [ClipboardCheck, "Documented plans", "Goals and progress, written"],
          ].map(([Ico, t, d], i) => (
            <div key={t} className="alpha-card p-6 text-center">
              <Ico className="w-6 h-6 text-[color:var(--alpha-purple-600)] mx-auto" />
              <div className="mt-3 font-display font-semibold">{t}</div>
              <div className="text-xs text-[color:var(--alpha-mute)] mt-1">{d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="alpha-section">
        <div className="alpha-container max-w-3xl">
          <div className="alpha-overline">FAQ</div>
          <h2 className="mt-3 font-display text-3xl font-medium">Common questions</h2>
          <div className="mt-6 space-y-3">
            {COMMON_FAQS.map((f, i) => (
              <details key={f.q} className="alpha-card p-0 overflow-hidden" data-testid={`landing-faq-${i}`}>
                <summary className="list-none p-5 flex items-center justify-between cursor-pointer">
                  <span className="font-display text-base font-semibold pr-4">{f.q}</span>
                  <span className="text-[color:var(--alpha-purple-600)] font-bold">+</span>
                </summary>
                <div className="px-5 pb-5 text-sm text-[color:var(--alpha-mute)] leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="alpha-section">
        <div className="alpha-container">
          <div className="relative overflow-hidden rounded-[28px] alpha-gradient p-10 sm:p-14 text-white">
            <div className="relative grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <h2 className="font-display text-3xl sm:text-4xl lg:text-[44px] font-medium leading-tight !text-white">
                  Ready to start {base.short.toLowerCase()}{city ? ` in ${city.name}` : ""}?
                </h2>
                <p className="mt-4 text-white/85 max-w-2xl">Call, email, or submit a referral. Our intake team responds within one business day.</p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3">
                <Link to="/referrals" className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-4 font-semibold text-center">Make a Referral</Link>
                <Link to="/contact" className="bg-[color:var(--alpha-green-600)] text-white rounded-full px-6 py-4 font-semibold text-center hover:bg-[color:var(--alpha-green-700)] transition">Talk to intake</Link>
              </div>
            </div>
          </div>

          {/* Related city pages */}
          {!city && (
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <span className="text-sm text-[color:var(--alpha-mute)]">Browse by city:</span>
              {Object.values(LANDING_CITIES).map((c) => (
                <Link key={c.slug} to={`/${slug}-${c.slug}`} className="alpha-chip" data-testid={`landing-city-${c.slug}`}>
                  {base.short} in {c.name} →
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
